import React, { useMemo, useState } from "react";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { useLanguage } from "../../Language";
import { Button, SxProps } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { ResourceView } from "../../../dto/ResourceView";
import { useDownloadableSource } from "./DownloadableSource";
import {
  DownloadableCheckoutDialog,
  EResourceCheckoutResponse,
} from "./downloadable/DownloadableCheckoutDialog";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { DownloadableCheckoutSuccessDialog } from "./downloadable/DownloadableCheckoutSuccessDialog";
import {
  getDownloadURL,
  OverdriveIFrameDialog,
} from "./downloadable/OverdriveIFrameDialog";
import { useAccount } from "../../Account";
import { AutoSyncSuccessDialog } from "./downloadable/AutoSyncSuccessDialog";
import { useToast } from "../../../contexts/ToastContext";
import { DownloadableHoldDialog } from "./downloadable/DownloadableHoldDialog";
import { AvailabilitySourceService } from "../../../contexts/EResourceAvailabilityContext";

interface DownloadButtonProps {
  resource: ResourceDWR | ResourceView;
  onClickDownloadableLink?: (
    event: React.MouseEvent,
    resource: ResourceDWR | ResourceView
  ) => void;
  /**
   * @deprecated
   */
  onClickCheckoutDownloadable?: (
    event: React.MouseEvent,
    resource: ResourceDWR | ResourceView
  ) => void;
  sx?: SxProps;
}

export const DownloadButton = ({
  resource,
  sx,
  onClickDownloadableLink,
}: DownloadButtonProps) => {
  const { translate } = useLanguage();
  const { showToast } = useToast();
  const { patron } = useAccount();
  const { source, loading, hasDownloadbleCheckouts } =
    useDownloadableSource(resource);
  const [open, setOpen] = useState<boolean>(false);
  const [showPlaceHoldDialog, setShowPlaceHoldDialog] =
    useState<boolean>(false);
  const [successfulCheckout, setSuccessfulCheckout] =
    useState<EResourceCheckoutResponse | null>(null);
  const showCheckoutSuccessDialog = useMemo(
    () => Boolean(successfulCheckout),
    [successfulCheckout]
  );

  const [autoSyncSuccessResponse, setAutoSyncSuccessResponse] =
    useState<EResourceCheckoutResponse | null>();
  const showAutoSyncSuccessDialog = useMemo(
    () => Boolean(autoSyncSuccessResponse),
    [autoSyncSuccessResponse]
  );

  const [overdriveDownload, setOverdriveDownload] =
    useState<EResourceCheckoutResponse | null>(null);
  const showOverdriveIFrameDialog = useMemo(
    () => Boolean(overdriveDownload),
    [overdriveDownload]
  );

  const downloadURL = useMemo(
    () => getDownloadURL(overdriveDownload, patron),
    [overdriveDownload, patron]
  );

  if (!resource.downloadable) {
    console.warn("Incorrect use of DownloadButton");
    return null;
  }

  const onClickFullEIntegration = () => {
    // TODO replace with a LoginContext hook
    LoginClientManager.promptIfNotLoggedIn(() => {
      // FIXME there was some logic for getAvailabilityForOneEResource
      // when NOT ( event.resource.eResourceFormats.length > 0 || (event.resource.eResourceService != "overdrive" && event.referrer != "holds" ) )
      if (source.availability) {
        if (!source.formatInfo?.length) {
          showToast(translate("eResourceNoFormatsMessage"), "error");
          return;
        }

        // checkout
        setOpen(true);
      } else {
        // place hold
        setShowPlaceHoldDialog(true);
      }
    });
  };

  // FIXME handle availability has changed (recheck source (cache bust))
  // FIXME handle bad borrower
  // TODO split Downloadable that supports 3rd party checkouts from the rest of DownloadButton
  // TODO handle Holds use case once refactored

  // Downloadable that supports 3rd party checkouts
  if (hasDownloadbleCheckouts) {
    return (
      <>
        <DownloadableHoldDialog
          resource={resource}
          source={source}
          open={showPlaceHoldDialog}
          onClose={() => setShowPlaceHoldDialog(false)}
        />
        <OverdriveIFrameDialog
          src={downloadURL}
          open={showOverdriveIFrameDialog}
          onClose={() => setOverdriveDownload(null)}
        />
        <DownloadableCheckoutSuccessDialog
          response={successfulCheckout}
          open={showCheckoutSuccessDialog}
          onClickDownload={() => {
            if (
              successfulCheckout.service === AvailabilitySourceService.overdrive
            ) {
              setOverdriveDownload(successfulCheckout);
            } else {
              const url = getDownloadURL(successfulCheckout, patron);
              const openWindow = window.open(
                url,
                "_blank",
                "noopener,noreferrer"
              );
              if (!openWindow) {
                console.error("Please enable popups");
              }
            }
            setSuccessfulCheckout(null);
          }}
          onClose={() => setSuccessfulCheckout(null)}
        />
        <AutoSyncSuccessDialog
          open={showAutoSyncSuccessDialog}
          response={autoSyncSuccessResponse}
          onClose={() => {
            setAutoSyncSuccessResponse(null);
          }}
        />
        <DownloadableCheckoutDialog
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          onSuccess={(response) => {
            if (response.autoSync) {
              setAutoSyncSuccessResponse(response);
            } else {
              setSuccessfulCheckout(response);
            }
            setOpen(false);
          }}
          resource={resource}
          source={source}
        />
        <Button
          sx={{ ...sx }}
          variant="contained"
          loading={loading}
          startIcon={source?.availability ? <DownloadIcon /> : <ScheduleIcon />}
          onClick={onClickFullEIntegration}
        >
          {source?.availability && translate("download")}
          {!source?.availability && translate("placeRequest")}
        </Button>
      </>
    );
  }

  // Regular Downloadable - treated as an external link
  return (
    <Button
      sx={{ ...sx, whiteSpace: "nowrap" }}
      variant="contained"
      href={resource.link}
      loading={loading}
      startIcon={source?.availability ? <DownloadIcon /> : <OpenInNewIcon />}
      target={"_blank"}
      onClick={(event) =>
        onClickDownloadableLink && onClickDownloadableLink(event, resource)
      } // only for Analytics purposes
    >
      {source?.availability && translate("download")}
      {!source?.availability && translate("checkDigitalLocations")}
    </Button>
  );
};
